import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Search, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import useBlogs from '../../hooks/useBlogs';
import { useAuth } from '../../context/AuthContext';

const BlogList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('blogs.create');
  const canUpdate = hasPermission('blogs.update');
  const canDelete = hasPermission('blogs.delete');
  const canChangeStatus = hasPermission('blogs.change_status');
  const { blogs, loading, deleteBlog, updateBlogStatus } = useBlogs();
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = blogs.filter(b =>
    (b.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.slug || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleStatus = async (blog) => {
    await updateBlogStatus(
      blog.id,
      blog.status === 'active' ? 'deactive' : 'active',
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Blogs</h1>
          <p className="text-slate-500 mt-1 font-medium">Manage blog posts</p>
        </div>
        {canCreate && <Button onClick={() => navigate('/blogs/add')} className="bg-primary text-secondary rounded-2xl px-6 py-3">
          <Plus size={18} className="mr-2" /> New Blog
        </Button>}
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input placeholder="Search by title or slug..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 rounded-2xl" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead>Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(item => (
                <TableRow key={item.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="h-16 w-24 overflow-hidden rounded-lg bg-slate-100 border border-slate-200">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                          No image
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-900">{item.title}</span>
                      <span className="text-xs text-slate-500">{item.meta_title}</span>
                    </div>
                  </TableCell>
                  <TableCell>/ {item.slug}</TableCell>
                  <TableCell>
                    <Badge className={item.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-200 text-slate-700 hover:bg-slate-200'}>
                      {item.status === 'active' ? 'Active' : 'Deactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canChangeStatus && <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(item)}
                        className="rounded-full px-3 text-xs"
                      >
                        {item.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>}
                      {canUpdate && <Button variant="ghost" size="sm" onClick={() => navigate(`/blogs/edit/${item.id}`)} className="h-8 w-8 p-0 rounded-full">
                        <Pencil size={16} />
                      </Button>}
                      {canDelete && <Button variant="ghost" size="sm" onClick={() => deleteBlog(item.id)} className="h-8 w-8 p-0 rounded-full">
                        <Trash2 size={16} />
                      </Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default BlogList;
